import type {
  BrowserClientMessage,
  BrowserServerMessage,
} from '@repo/contracts/browser-protocol';

import { ApiError } from '../api/client';
import { logger } from '../lib/logger';
import { fetchBrowserTicket, workerSocketUrl } from './browser-ticket-api';
import type { BrowserTransport, TransportOptions } from './transport';

const log = logger.child({ module: 'browser-ws-transport' });

/**
 * The real transport. Two steps, in this order, every time:
 *
 *   1. `POST /api/canvas/browser-ticket` — the API checks the caller's session
 *      and signs a short-lived JWT with the secret the worker verifies.
 *   2. open `WS <workerUrl>/live?token=…` — the worker verifies that JWT and
 *      answers `ready`. That message, not the socket opening, is the go signal.
 *
 * The token rides as a query param because a browser WebSocket cannot set
 * request headers — that is the worker's own documented reason for accepting
 * `?token=` (apps/browser/src/auth.ts), not a shortcut here.
 *
 * Step 1 is async but `createWsTransport` is not: callers get a transport
 * immediately and anything they send before the socket is open is queued and
 * flushed on open. Without that, the viewport/navigate pair the session hook
 * sends on mount would be dropped on the floor and the browser would sit on
 * about:blank forever.
 */
export function createWsTransport({
  handlers,
}: TransportOptions): BrowserTransport {
  let socket: WebSocket | null = null;
  let disposed = false;
  const queue: BrowserClientMessage[] = [];

  const flush = () => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    while (queue.length > 0) {
      const message = queue.shift();
      if (message) socket.send(JSON.stringify(message));
    }
  };

  const connect = async () => {
    const ticket = await fetchBrowserTicket();
    if (disposed) return;

    log.debug({ sessionId: ticket.sessionId }, 'browser ticket minted');

    const next = new WebSocket(workerSocketUrl(ticket));
    socket = next;

    // Distinguishes "never connected" from "connected, then closed" — the close
    // event fires for both and only this tells them apart.
    let opened = false;

    next.addEventListener('open', () => {
      opened = true;
      flush();
    });

    next.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;
      try {
        handlers.onMessage(JSON.parse(event.data) as BrowserServerMessage);
      } catch (err) {
        log.warn({ err }, 'unparseable frame from browser worker');
      }
    });

    next.addEventListener('close', (event) => {
      if (disposed) return;
      if (!opened) {
        // A close before open with no code is a failed handshake — the worker
        // is down, or something between here and it refused the upgrade.
        handlers.onFailure(
          'The browser service did not accept the connection.',
        );
        return;
      }
      handlers.onClose({ clean: event.wasClean, code: event.code });
    });

    next.addEventListener('error', () => {
      if (!opened && !disposed) {
        handlers.onFailure('Could not reach the browser service.');
      }
    });
  };

  connect().catch((err: unknown) => {
    if (disposed) return;
    log.error({ err }, 'browser session could not be started');
    handlers.onFailure(
      // 401 means the app session went stale, which is a different problem from
      // the worker being down, and the session hook maps it to its own screen.
      err instanceof ApiError && err.status === 401
        ? 'Your session expired.'
        : 'Could not start a browser session.',
      err instanceof ApiError && err.status === 401
        ? 'unauthorized'
        : undefined,
    );
  });

  return {
    close: () => {
      disposed = true;
      socket?.close();
      socket = null;
    },
    send: (message) => {
      if (disposed) return;
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(message));
        return;
      }
      queue.push(message);
    },
  };
}
